import { useSyncExternalStore } from "react";
import type { DropAnimationFunction, UniqueIdentifier } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
    getReducedMotionQuery,
    isReducedMotionPreferred,
} from "@helpers/reducedMotion";

export interface BoardMotionTokens {
    easing: string;
    durationMs: number;
}

export interface BoardMotion extends BoardMotionTokens {
    isReduced: boolean;
}

const FALLBACK_EASING = "cubic-bezier(0.23, 1, 0.32, 1)";
const FALLBACK_DURATION_MS = 200;

/** Token defaults until the first drag reads the live values. */
export const DEFAULT_BOARD_MOTION_TOKENS: BoardMotionTokens = {
    easing: FALLBACK_EASING,
    durationMs: FALLBACK_DURATION_MS,
};

const subscribe = (listener: () => void) => {
    const query = getReducedMotionQuery();
    query?.addEventListener("change", listener);
    return () => query?.removeEventListener("change", listener);
};

const getSnapshot = isReducedMotionPreferred;
const getServerSnapshot = () => false;

/** The OS reduced motion setting, re-rendering when it changes. */
export const useIsReducedMotion = () =>
    useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

/**
 * dnd-kit animates neighbours and the drop in JS, so it needs the values of
 * `--ease-out` and `--duration-slow` rather than the variables. The tokens
 * are read per drag, so a runtime override of them (the playground's Slow
 * motion toggle) applies.
 */
export const readBoardMotionTokens = (): BoardMotionTokens => {
    // A probe lets the browser resolve tokens that are calc() expressions.
    const probe = document.createElement("div");
    probe.style.transition = "transform var(--duration-slow) var(--ease-out)";
    document.body.append(probe);
    const { transitionDuration, transitionTimingFunction } =
        getComputedStyle(probe);
    probe.remove();
    const durationSeconds = Number.parseFloat(transitionDuration);
    return {
        easing: transitionTimingFunction.startsWith("cubic-bezier")
            ? transitionTimingFunction
            : FALLBACK_EASING,
        durationMs:
            Number.isFinite(durationSeconds) && durationSeconds > 0
                ? durationSeconds * 1000
                : FALLBACK_DURATION_MS,
    };
};

/**
 * The tokens plus the reduced motion setting as it is now, for motion that
 * starts outside render (a card's entrance). Rendered motion (neighbour
 * shifts, the drop) follows the setting through `useIsReducedMotion`, so
 * toggling it applies without a reload either way.
 */
export const readBoardMotion = (): BoardMotion => ({
    ...readBoardMotionTokens(),
    isReduced: getSnapshot(),
});

/** Neighbour shift; null (instant) under reduced motion. */
export const getSortableTransition = (motion: BoardMotion) =>
    motion.isReduced
        ? null
        : { duration: motion.durationMs, easing: motion.easing };

/** A column scrolling a dropped card into view, which takes this long. */
export const getLandingDurationMs = (motion: BoardMotionTokens) =>
    motion.durationMs * 1.5;

/** A dropped card's column scrolling it into view. */
export interface BoardDropLanding {
    /** No part of the card showed when it dropped. */
    wasOutOfView: boolean;
    finished: Promise<void>;
}

interface BoardDropAnimationArgs {
    motion: BoardMotion;
    /** Added to the overlay so its lift eases back while it travels. */
    settlingClassName: string;
    /** Starts scrolling the dropped card into view; null when it's in view. */
    land: (node: HTMLElement) => BoardDropLanding | null;
    onSettled: (activeId: UniqueIdentifier) => void;
}

const FADE_OUT: Keyframe[] = [{ opacity: 1 }, { opacity: 0 }];

/**
 * The overlay settles into the slot; `onSettled` runs once it lands (at once
 * under reduced motion) so the board can swap the slot for the card. A card
 * dropped outside its column's view (`active.rect` is where it will show)
 * lands as the column scrolls it in. When none of it showed, the overlay
 * fades where it was let go rather than flying ahead of the scroll.
 */
export const createBoardDropAnimation =
    ({
        motion,
        settlingClassName,
        land,
        onSettled,
    }: BoardDropAnimationArgs): DropAnimationFunction =>
    async ({ active, dragOverlay, transform }) => {
        try {
            const landing = land(active.node);
            if (
                motion.isReduced ||
                typeof dragOverlay.node.animate !== "function"
            ) {
                return;
            }
            dragOverlay.node.classList.add(settlingClassName);
            const finalTransform = {
                ...transform,
                x: transform.x - (dragOverlay.rect.left - active.rect.left),
                y: transform.y - (dragOverlay.rect.top - active.rect.top),
            };
            const overlayAnimation = landing?.wasOutOfView
                ? dragOverlay.node.animate(FADE_OUT, {
                      duration: motion.durationMs,
                      easing: motion.easing,
                      fill: "forwards",
                  })
                : dragOverlay.node.animate(
                      [
                          { transform: CSS.Transform.toString(transform) },
                          { transform: CSS.Transform.toString(finalTransform) },
                      ],
                      {
                          duration:
                              landing === null
                                  ? motion.durationMs
                                  : getLandingDurationMs(motion),
                          easing: motion.easing,
                          fill: "forwards",
                      },
                  );
            await Promise.all([overlayAnimation.finished, landing?.finished]);
        } finally {
            onSettled(active.id);
        }
    };
